import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LayerGroup, Marker, Polyline, Popup, CircleMarker } from 'react-leaflet'
import {
  getGpsAuditResults, resolveGpsAudit, gpsAuditTileUrl, GpsAuditResult,
} from '../services/api'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'

function fmt(m: number | null): string {
  if (m === null || m === undefined) return '—'
  return m >= 1609 ? `${(m / 1609.34).toFixed(2)} mi` : `${Math.round(m)} m`
}

export default function GpsAuditLayer() {
  const role = useRole()
  const queryClient = useQueryClient()
  const [pendingSerial, setPendingSerial] = useState<string | null>(null)
  const { data: results } = useQuery({
    queryKey: ['gpsAuditResults'],
    queryFn: async () => (await getGpsAuditResults('flagged,eligible')).data,
    refetchInterval: 60_000,
  })

  const resolveMutation = useMutation({
    mutationFn: ({ serial, action }: { serial: string; action: 'apply' | 'dismiss' }) =>
      resolveGpsAudit(serial, action),
    onMutate: (vars: { serial: string; action: 'apply' | 'dismiss' }) => setPendingSerial(vars.serial),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpsAuditResults'] })
      queryClient.invalidateQueries({ queryKey: ['mapRadios'] })
    },
    onSettled: () => setPendingSerial(null),
  })

  // Anchor on the Splynx pin: it always exists for a reviewable verdict and is
  // the thing the operator judges against the satellite tile. The radio point
  // may be null (e.g. a 5 GHz radio with no/bad position — exactly the pin we
  // want to push), so the connector line + radio dot render only when present.
  const rows = (results ?? []).filter(
    (r: GpsAuditResult) => r.splynx_lat !== null && r.splynx_lng !== null,
  )

  return (
    <LayerGroup>
      {rows.map((r: GpsAuditResult) => {
        const flagged = r.status === 'flagged'
        const color = flagged ? '#dc2626' : '#d97706' // red flagged, amber eligible
        const hasRadio = r.radio_lat !== null && r.radio_lng !== null
        return (
          <LayerGroup key={r.serial_number}>
            {hasRadio && (
              <Polyline
                positions={[[r.splynx_lat!, r.splynx_lng!], [r.radio_lat!, r.radio_lng!]]}
                pathOptions={{ color, weight: 2, dashArray: flagged ? undefined : '6 6' }}
              />
            )}
            {hasRadio && (
              <CircleMarker
                center={[r.radio_lat!, r.radio_lng!]}
                radius={4}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.9 }}
              />
            )}
            <Marker position={[r.splynx_lat!, r.splynx_lng!]}>
              <Popup maxWidth={380}>
                <div style={{ minWidth: 320 }}>
                  <strong>{r.serial_number}</strong> — {r.customer_name ?? 'unknown'}
                  <span style={{
                    marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 11,
                    background: flagged ? '#fee2e2' : '#fef3c7',
                    color: flagged ? '#b91c1c' : '#92400e',
                  }}>{r.status.toUpperCase()}</span>
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    <div>Direction: {r.direction === 'to_radio' ? 'push pin → radio (5 GHz)' : 'radio fix → Splynx (6 GHz)'}</div>
                    <div>Pin↔radio: {fmt(r.discrepancy_m)} | geocode↔pin: {fmt(r.geocode_distance_m)}</div>
                    <div>RF range: {fmt(r.rf_range_m)} | pin→BN: {fmt(r.pin_to_bn_m)}</div>
                    <div>Vision: {r.vision_structure === null ? '—' : r.vision_structure ? 'structure ✓' : 'NO structure'}
                      {r.vision_description ? ` — ${r.vision_description}` : ''}</div>
                    {r.reasons.length > 0 && <div>Reasons: {r.reasons.join(', ')}</div>}
                  </div>
                  <img
                    src={gpsAuditTileUrl(r.serial_number)}
                    alt="judged satellite tile"
                    style={{ width: '100%', marginTop: 6, borderRadius: 4 }}
                  />
                  {hasRole(role, 'admin') && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => resolveMutation.mutate({ serial: r.serial_number, action: 'apply' })}
                        disabled={pendingSerial === r.serial_number}
                        style={{ padding: '4px 10px', background: '#2563eb', color: '#fff', borderRadius: 4, border: 0, cursor: 'pointer' }}
                      >Apply anyway</button>
                      <button
                        onClick={() => resolveMutation.mutate({ serial: r.serial_number, action: 'dismiss' })}
                        disabled={pendingSerial === r.serial_number}
                        style={{ padding: '4px 10px', background: '#e5e7eb', borderRadius: 4, border: 0, cursor: 'pointer' }}
                      >Dismiss</button>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          </LayerGroup>
        )
      })}
    </LayerGroup>
  )
}
